import { useState, type FormEvent } from 'react';
import { Badge, Button, Checkbox, Code, Group, Modal, Radio, Stack, Switch, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { attachOutputs, useAnalysisType, useInvalidateAnalyses, type AnalysisStaff, type AttachOutputsBody, type OutputDecl } from '@/api/analyses';
import { useProjectObjects, type DataObject } from '@/api/objects';
import { useFormat } from '@/components/useFormat';
import { objectName } from '@/features/objects/objectName';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { AnalysisChangePreview } from '@/features/staff/AttachPreview';
import { useRehearsal } from '@/features/staff/useRehearsal';
import { brand } from '@/theme/theme';

type Mode = 'outputs' | 'extra';

/** Whether outputs can stand in for running the analysis (AN16): never launched and nothing attached yet. */
export function canAttachInsteadOfRun(a: AnalysisStaff): boolean {
  const state = a.state ?? '';
  return (state === 'planned' || state === 'ready') && (a.runs ?? []).length === 0 && !(a.all_outputs ?? []).some((o) => !o.internal);
}

/**
 * The project's objects that may become this analysis's outputs, as the backend checks them: ready,
 * referenced from a bucket, nobody's output yet, not one of its inputs, of a kind the type outputs.
 */
export function attachableObjects(objects: DataObject[], analysis: AnalysisStaff, outputKinds: Set<string>): DataObject[] {
  const inputs = new Set((analysis.inputs ?? []).map((i) => i.data_object?.public_id).filter(Boolean));
  return objects.filter((o) => o.state === 'ready' && !o.deleted && o.origin === 'referenced' && !o.analysis && !inputs.has(o.public_id) && outputKinds.has(o.kind));
}

/**
 * Staff give an analysis outputs that already exist in its project — data registered from a bucket —
 * either instead of running it (→ review) or added to it in any live state without moving it. "Check"
 * rehearses the attach and shows the analysis as it would be; only then can it be committed.
 */
export function AttachOutputsModal({ analysis, opened, onClose }: { analysis: AnalysisStaff; opened: boolean; onClose: () => void }) {
  const { t } = useTranslation('analyses');
  const { t: tc } = useTranslation();
  const { bytes } = useFormat();
  const kindLabel = useKindLabel();
  const invalidate = useInvalidateAnalyses();
  const { data: type } = useAnalysisType(analysis.type_key);
  const objects = useProjectObjects(opened ? analysis.project : undefined);
  const insteadOfRun = canAttachInsteadOfRun(analysis);
  const [chosenMode, setMode] = useState<Mode>(insteadOfRun ? 'outputs' : 'extra');
  const mode: Mode = insteadOfRun ? chosenMode : 'extra';
  const [selected, setSelected] = useState<string[]>([]);
  const [deliverable, setDeliverable] = useState(false);
  const [note, setNote] = useState('');

  const decls = (type?.outputs as OutputDecl[] | undefined) ?? [];
  const outputKinds = new Set(decls.map((o) => o.kind));
  const deliverableKinds = new Set(decls.filter((o) => o.deliverable).map((o) => o.kind));
  const candidates = attachableObjects(objects.data ?? [], analysis, outputKinds);
  const chosen = candidates.filter((o) => selected.includes(o.public_id));
  const covered = new Set<string>(chosen.map((o) => o.kind));
  const missing = [...deliverableKinds].filter((k) => !covered.has(k));
  const notDeliverable = chosen.filter((o) => !deliverableKinds.has(o.kind));

  const body: AttachOutputsBody = { data_objects: selected, note, deliverable: mode === 'extra' && deliverable };
  const r = useRehearsal((dryRun) => attachOutputs(analysis.public_id, mode, body, dryRun), { ...body, mode });

  const handleClose = () => {
    setSelected([]);
    setDeliverable(false);
    setNote('');
    r.reset();
    onClose();
  };
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!r.preview) {
      await r.check();
      return;
    }
    const done = await r.commit();
    if (!done) return;
    invalidate(analysis.public_id, analysis.project);
    notifications.show({ color: 'forest', message: t('attach.done', { count: selected.length }) });
    handleClose();
  };
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Modal opened={opened} onClose={handleClose} title={t('attach.title')} size="xl">
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          {insteadOfRun ? (
            <Radio.Group label={t('attach.mode')} value={mode} onChange={(v) => setMode(v as Mode)}>
              <Stack gap={6} mt={4}>
                <Radio value="outputs" label={t('attach.modeOutputs')} description={t('attach.modeOutputsHint')} />
                <Radio value="extra" label={t('attach.modeExtra')} description={t('attach.modeExtraHint')} />
              </Stack>
            </Radio.Group>
          ) : (
            <Text fz={13} c={brand.textMuted}>
              {t('attach.extraOnly', { state: analysis.state })}
            </Text>
          )}

          <Stack gap={4}>
            <Text fz={14} fw={600}>
              {t('attach.objects')}
            </Text>
            <Text fz={12} c={brand.textMuted}>
              {t('attach.objectsHint', { kinds: [...outputKinds].map(kindLabel).join(', ') || '—' })}
            </Text>
            {objects.isPending ? (
              <Text fz={13}>{tc('common.loading')}</Text>
            ) : candidates.length === 0 ? (
              <Text fz={13} c={brand.textMuted} data-testid="no-candidates">
                {t('attach.noCandidates')}
              </Text>
            ) : (
              <Table fz={13} verticalSpacing={4}>
                <Table.Tbody>
                  {candidates.map((o) => (
                    <Table.Tr key={o.public_id}>
                      <Table.Td w={32}>
                        <Checkbox aria-label={objectName(o)} checked={selected.includes(o.public_id)} onChange={() => toggle(o.public_id)} />
                      </Table.Td>
                      <Table.Td>
                        <Text fz={13} fw={600}>
                          {objectName(o)}
                        </Text>
                        <Text fz={12} c={brand.textMuted}>
                          {kindLabel(o.kind)} · {o.public_id}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Code fz={11}>{o.processed_uri || o.raw_uri}</Code>
                      </Table.Td>
                      <Table.Td ta="right">
                        {o.file_count} · {bytes(o.total_bytes ?? 0)}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {r.errors.data_objects && (
              <Text c="red" fz={13} role="alert">
                {r.errors.data_objects}
              </Text>
            )}
          </Stack>

          {mode === 'outputs' && deliverableKinds.size > 0 && (
            <Group gap={6} data-testid="coverage">
              <Text fz={13}>{t('attach.coverage')}</Text>
              {[...deliverableKinds].map((k) => (
                <Badge key={k} variant="light" color={covered.has(k) ? 'forest' : 'red'}>
                  {kindLabel(k)}
                </Badge>
              ))}
              {missing.length > 0 && (
                <Text fz={12} c={brand.textMuted}>
                  {t('attach.missing')}
                </Text>
              )}
            </Group>
          )}
          {mode === 'extra' && (
            <Switch
              label={t('attach.deliverable')}
              description={t(analysis.state === 'delivered' ? 'attach.deliverableNowHint' : 'attach.deliverableHint')}
              checked={deliverable}
              onChange={(e) => setDeliverable(e.currentTarget.checked)}
              error={r.errors.deliverable ?? (deliverable && notDeliverable.length > 0 ? t('attach.notDeliverable', { names: notDeliverable.map(objectName).join(', ') }) : undefined)}
            />
          )}
          <TextInput label={t('attach.note')} description={t('attach.noteHint')} value={note} onChange={(e) => setNote(e.currentTarget.value)} maxLength={500} error={r.errors.note} />

          {r.preview && <AnalysisChangePreview before={analysis} after={r.preview} added={selected} />}
          {r.general && (
            <Text c="red" role="alert">
              {r.general}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {tc('common.cancel')}
            </Button>
            {r.preview ? (
              <Button type="submit" loading={r.pending === 'commit'}>
                {t('attach.commit')}
              </Button>
            ) : (
              <Button type="submit" variant="light" loading={r.pending === 'check'} disabled={selected.length === 0}>
                {t('attach.check')}
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
